import React from 'react';
import { useNavigate } from 'react-router-dom';
import { caseStudiesData } from '../data/caseStudiesData';
import { openSizingForm } from '../lib/sizingForm';
import SeasonalCapacityNote from './SeasonalCapacityNote';

const Solution = () => {
  const navigate = useNavigate();

  const handleCaseStudyClick = (studyId) => {
    navigate('/case-studies', { state: { studyId } });
  };
  const benefits = [
    'Proven Low-Temperature Capability',
    'Radiator Suitability',
    'Property Heat Loss',
    'Estimated Boiler Power Output',
    'Current Running Costs',
    'Estimated Future Running Costs with Clean Heat',
    'Changes You Can Make Now to Lower Bills',
  ];

  return (
    <section id="solution" className="section-padding">
      <div className="container-max px-4">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <p className="text-xs font-bold tracking-widest text-rebel-coral uppercase mb-4 font-sans" style={{maxWidth: 'none'}}>
            THE SOLUTION
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-semibold mb-8 text-rebel-dark leading-tight">
            Meet the fitness tracker for your heating system
          </h2>
          <div className="game-card p-12 mt-12">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-rebel-dark mb-6">
              Eliminating the <span className="font-extrabold italic">like-for-like replacement</span> cycle
            </h3>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">
              Most homes can already heat at low flow temperatures on their existing radiators. Rebel's sensor measures how your boiler performs <strong>before</strong> it fails, giving you the evidence to avoid unnecessary upgrades and keep install costs down.
            </p>
          </div>
        </div>

        {/* Benefits and Sensor Visual */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          {/* Left Column - Benefits */}
          <div className="game-card p-10">
            <h3 className="text-2xl sm:text-3xl font-bold text-rebel-dark mb-8">What you'll discover</h3>
            <div className="space-y-4 mb-12" role="list">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-6 h-6 text-rebel-green" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                  </div>
                  <span className="text-gray-600" role="listitem">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Key Features */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <h4 className="text-xl font-bold text-rebel-dark mb-4">Why it's better</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <span className="text-rebel-coral font-bold">→</span>
                  <span className="text-gray-600">Simple clip-on install - no skills required</span>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-rebel-coral font-bold">→</span>
                  <span className="text-gray-600">At least 10x more affordable than plumbed-in heat meters</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Sensor Visual */}
          <div className="text-center">
            <div className="game-card p-12">
              <h3 className="text-2xl sm:text-3xl font-bold text-rebel-dark mb-4">No Plumbing Required</h3>
              <p className="text-base sm:text-lg text-gray-600 mb-4 leading-relaxed">
                Advanced passive flow sensing technology that clips onto your existing boiler pipes with no specialist skills.
              </p>
              <p className="text-lg sm:text-xl font-semibold text-rebel-coral mb-6 italic">
                Works with any heating system!
              </p>
              <div className="flex items-center justify-center mb-4">
                <svg className="w-16 h-16 text-rebel-coral" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                </svg>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                <strong>Simple enough to send in the post.</strong> We guide you through the install, you connect to your WiFi, and data starts being collected. <strong>Results within days.</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Installer Focus Section */}
        <div className="mb-20">
          <div className="w-full max-w-4xl mx-auto text-center mb-12">
            <p className="text-xs font-bold tracking-widest text-rebel-coral uppercase mb-4 font-sans" style={{maxWidth: 'none'}}>
              FOR INSTALLERS & HEATING PROFESSIONALS
            </p>
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-rebel-dark mb-6 leading-tight">Quote with confidence, install with precision</h3>
            <p className="text-lg sm:text-xl text-gray-600 leading-relaxed w-full" style={{maxWidth: 'none'}}>
              Real data from the existing heating system shows whether a building can already run at low temperature. Most can, so you can size heat pumps accurately and avoid quoting for upgrades that aren't needed. Works for homes, heritage buildings and churches.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="game-card p-8 text-center">
              <div className="w-16 h-16 bg-rebel-teal/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-rebel-teal" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-rebel-dark mb-4">Approach with confidence</h4>
              <p className="text-base text-gray-600 leading-relaxed">
                Real data from the customer's existing heating system means you can size and specify heat pumps with precision.
              </p>
            </div>

            <div className="game-card p-8 text-center">
              <div className="w-16 h-16 bg-rebel-coral/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-rebel-coral" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-rebel-dark mb-4">De-risk projects in advance</h4>
              <p className="text-base text-gray-600 leading-relaxed">
                Show customers their existing radiators can cope, instead of replacing them to be safe.
              </p>
            </div>

            <div className="game-card p-8 text-center">
              <div className="w-16 h-16 bg-rebel-green/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-rebel-green" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                </svg>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-rebel-dark mb-4">Help customers adapt</h4>
              <p className="text-base text-gray-600 leading-relaxed">
                Let customers see their heating patterns before installation. Reduce post-install support calls and increase satisfaction.
              </p>
            </div>
          </div>
        </div>

        {/* Case Studies Section */}
        <div className="mb-20 px-4">
          <div className="w-full max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs font-bold tracking-widest text-rebel-coral uppercase mb-4 font-sans" style={{maxWidth: 'none'}}>
                CASE STUDIES
              </p>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-rebel-dark mb-6 leading-tight">
                Early Results
              </h3>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed w-full" style={{maxWidth: 'none'}}>
                Real-world deployments delivering measurable insights. These early case studies demonstrate the power of data-driven heat loss analysis.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {caseStudiesData.map((study) => (
              <button
                key={study.id}
                onClick={() => handleCaseStudyClick(study.id)}
                className="block cursor-pointer w-full text-left focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-rebel-coral rounded-3xl touch-manipulation"
                aria-label={`View case study: ${study.title}`}
              >
                <div className="game-card p-8 transition-all duration-300">
                  <div className="mb-6">
                    <span
                      className="inline-block px-4 py-1 rounded-full text-sm font-semibold mb-4"
                      style={{
                        backgroundColor: study.category === 'Residential' ? 'rgba(255, 107, 107, 0.2)' : 'rgba(78, 205, 196, 0.2)',
                        color: study.category === 'Residential' ? '#FF6B6B' : '#4ECDC4'
                      }}
                    >
                      {study.category}
                    </span>
                    <h4 className="text-xl sm:text-2xl font-bold text-rebel-dark mb-3 leading-tight">
                      {study.title}
                    </h4>
                    <p className="text-base sm:text-lg text-gray-600 mb-4 leading-relaxed">
                      {study.description}
                    </p>
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      {study.previewMetrics.map((metric, idx) => (
                        <div key={idx}>
                          <div className="text-2xl sm:text-3xl font-bold text-rebel-coral">{metric.value}</div>
                          <p className="text-sm sm:text-base text-gray-600 leading-snug">{metric.subtitle || metric.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-6 text-center">
                    <span className="bg-rebel-coral text-white px-6 py-3 rounded-full font-semibold hover:bg-rebel-orange transition-all duration-200 shadow-md hover:shadow-lg inline-block min-h-[44px] flex items-center justify-center">
                      Learn More →
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-rebel-coral to-rebel-orange rounded-3xl p-12 max-w-4xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6">
              Ready to get started?
            </h3>
            <button
              onClick={openSizingForm}
              className="bg-white text-rebel-coral px-10 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all duration-200 shadow-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white min-h-[44px] touch-manipulation"
              aria-label="Open the free heat pump sizing form"
            >
              Join the Waitlist
            </button>
            <SeasonalCapacityNote tone="light" className="mt-6" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solution;